import CauseList from "./CauseList";
import { useCause } from "contexts/CauseContext";
import { useTeam } from "contexts/TeamContext";
import { useNavigate, useLocation } from "react-router-dom";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FeelingCause } from "types/feeling-cause";
import { useMoodsTranslator } from "../features/settings/components/useMoodsTranslator";

type CausesProps = {
  /** When set (e.g. team details view), only team causes for this Firestore `teams` doc id are listed. */
  teamScopeId?: string;
  /** When set, render only these selected cause type ids (used by team details view mode). */
  selectedCauseTypeIds?: string[];
  view: boolean;
};

const Causes = ({ teamScopeId, selectedCauseTypeIds, view }: CausesProps) => {
  const {
    activeCauseTypes,
    defaultCauseTypes,
    setActiveCauseTypes,
    causeTypes,
  } = useCause();
  const { activeTeamCauseTypes, setActiveTeamCauseTypes, organizationId } =
    useTeam();
  const { t } = useTranslation();
  const moodsTranslator = useMoodsTranslator();
  const navigate = useNavigate();
  const { state } = useLocation();
  const type = state?.type;
  const [personalC, setPersonalC] = useState<FeelingCause[]>([]);
  const [teamC, setTeamC] = useState<FeelingCause[]>([]);
  const selectedCauseIds = useMemo(
    () =>
      new Set(
        (selectedCauseTypeIds || []).filter(
          (id): id is string => typeof id === "string" && id.length > 0
        )
      ),
    [selectedCauseTypeIds]
  );

  useEffect(() => {
    const merged = new Map();

    (defaultCauseTypes || []).forEach((cause: FeelingCause) =>
      merged.set(cause.id, cause)
    );

    (activeCauseTypes || []).forEach((cause: FeelingCause) =>
      merged.set(cause.id, cause)
    );
    setPersonalC(Array.from(merged.values()));
    const teamList = activeTeamCauseTypes || [];
    const scopedTeamCauses = teamScopeId
      ? teamList.filter(
          (cause: FeelingCause & { teamId?: string }) =>
            cause.teamId === teamScopeId
        )
      : teamList;

    if (selectedCauseIds.size === 0) {
      setTeamC(scopedTeamCauses);
      return;
    }

    const indexedCauses = new Map<string, FeelingCause>();
    [...(causeTypes || []), ...scopedTeamCauses].forEach(
      (cause: FeelingCause) => {
        if (cause?.id) {
          indexedCauses.set(String(cause.id), cause);
        }
      }
    );

    const selectedCauses = Array.from(selectedCauseIds)
      .map((causeId) => indexedCauses.get(causeId))
      .filter((cause): cause is FeelingCause => Boolean(cause));

    setTeamC(selectedCauses);
  }, [
    activeTeamCauseTypes,
    activeCauseTypes,
    causeTypes,
    defaultCauseTypes,
    selectedCauseIds,
    type,
    teamScopeId,
  ]);

  return (
    <div>
      {organizationId && (
        <>
          <CauseList
            moodsTranslator={moodsTranslator}
            causeTypes={teamC}
            organizationId={organizationId}
            t={t}
            type={type}
            setActiveCauseTypes={setActiveCauseTypes}
            setActiveTeamCauseTypes={setActiveTeamCauseTypes}
            view={view}
          />
        </>
      )}
    </div>
  );
};

export default Causes;
